/**
 * Reading the training API's response envelope.
 *
 * The two backends do NOT answer the same shape, and the difference is one
 * level deep and completely silent:
 *
 *   management API   ->  the payload itself, or {message, data}
 *   training  API    ->  {success, message, data: <the payload>}
 *
 * `/cluster/auto` is a training-API route, so its assignments are at
 * `response.data.data.assignments`. Reading `response.data.assignments` yields
 * `undefined` — no throw, no console error, nothing in the network tab. Every
 * image then gets `cluster_id: undefined`, lands in one bucket, and a user who
 * asked for three classes gets one, with nothing anywhere saying why.
 *
 * It lives in its own module, free of axios, purely so it can be tested: any
 * suite that imports the API module inherits the ESM failure that already
 * breaks `src/App.test.js`.
 */

/** Thrown when the training API answers with a failure envelope. */
export class ApiEnvelopeError extends Error {
  constructor(message) {
    super(message || 'The request failed.');
    this.name = 'ApiEnvelopeError';
  }
}

/**
 * Unwrap a training-API envelope, or throw with the server's own message.
 *
 * `error_response` returns HTTP 500 with `{success: false, message}`, so axios
 * rejects — but it can also return a 200 whose `success` is false, which a
 * status check alone would sail straight past. Hence checking the flag.
 */
export const unwrapEnvelope = (body, require = []) => {
  if (!body || typeof body !== 'object') {
    throw new ApiEnvelopeError('The server returned an empty response.');
  }
  if (body.success !== true) {
    throw new ApiEnvelopeError(body.message);
  }
  const data = body.data;
  if (!data || typeof data !== 'object') {
    throw new ApiEnvelopeError(body.message || 'The server returned no data.');
  }
  require.forEach((key) => {
    if (!(key in data)) {
      throw new ApiEnvelopeError(`The server response is missing "${key}".`);
    }
  });
  return data;
};

export default unwrapEnvelope;
